import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  GoogleMap,
  DirectionsService,
  DirectionsRenderer,
  useJsApiLoader
} from '@react-google-maps/api';
import { Plus, Trash2, MapPin } from 'lucide-react';
import './add-route-modal.css'; 
import { GOOGLE_MAPS_ID, GOOGLE_MAPS_LIBRARIES } from '../../config/googleMaps';

const mapContainerStyle = {
  width: '100%',
  height: '100%',
  minHeight: '400px',
  borderRadius: '8px'
};

const defaultCenter = { lat: 28.6139, lng: 77.2090 }; // Delhi

const normalizeRoutePathPoint = (point) => {
  if (!point) return null;
  const latValue = typeof point.lat === 'function' ? point.lat() : point.lat ?? point.latitude;
  const lngValue = typeof point.lng === 'function' ? point.lng() : point.lng ?? point.longitude;
  const lat = Number(latValue);
  const lng = Number(lngValue);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};

const extractSelectedRoutePath = (route) => {
  const overviewPath = Array.isArray(route?.overview_path) ? route.overview_path : [];
  const normalizedOverview = overviewPath.map(normalizeRoutePathPoint).filter(Boolean);
  if (normalizedOverview.length >= 2) return normalizedOverview;

  const legs = Array.isArray(route?.legs) ? route.legs : [];
  const fromSteps = legs
    .flatMap((leg) => (Array.isArray(leg?.steps) ? leg.steps : []))
    .flatMap((step) => {
      const stepPath = Array.isArray(step?.path) ? step.path : [];
      return stepPath.map(normalizeRoutePathPoint).filter(Boolean);
    });

  return fromSteps.length >= 2 ? fromSteps : [];
};

const AddRouteModal = ({ onClose, onSubmit }) => {
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_ID,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES
  });

  // ✅ FORM STATE (As per your original)
  const [routeName, setRouteName] = useState('');
  const [source, setSource] = useState(null);        
  const [destination, setDestination] = useState(null); 
  const [stops, setStops] = useState([]);
  const [sourceInput, setSourceInput] = useState('');
  const [destinationInput, setDestinationInput] = useState('');
  const [currentStop, setCurrentStop] = useState('');
  const [sourceSuggestions, setSourceSuggestions] = useState([]);
  const [destinationSuggestions, setDestinationSuggestions] = useState([]);
  const [stopSuggestions, setStopSuggestions] = useState([]);
  const [activeSuggestionField, setActiveSuggestionField] = useState('');

  // ✅ MAP STATE
  const [directionsResponse, setDirectionsResponse] = useState(null);

  // ✅ REFS
  const autocompleteServiceRef = useRef(null);
  const geocoderRef = useRef(null);
  const predictionTimerRef = useRef({ source: null, destination: null, stop: null });

  useEffect(() => {
    if (!isLoaded || !window.google?.maps?.places) return;
    autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    geocoderRef.current = new window.google.maps.Geocoder();
  }, [isLoaded]);

  const fetchPredictions = useCallback((value, field) => {
    const query = String(value || '').trim();
    if (!query || !autocompleteServiceRef.current) {
      if (field === 'source') setSourceSuggestions([]);
      if (field === 'destination') setDestinationSuggestions([]);
      if (field === 'stop') setStopSuggestions([]);
      return;
    }

    autocompleteServiceRef.current.getPlacePredictions(
      {
        input: query,
        types: ['geocode'],
        componentRestrictions: { country: 'in' },
      },
      (predictions = [], status) => {
        const options =
          status === window.google.maps.places.PlacesServiceStatus.OK
            ? predictions.slice(0, 6)
            : [];

        if (field === 'source') setSourceSuggestions(options);
        if (field === 'destination') setDestinationSuggestions(options);
        if (field === 'stop') setStopSuggestions(options);
      }
    );
  }, []);

  const schedulePredictions = useCallback((value, field) => {
    if (predictionTimerRef.current[field]) {
      clearTimeout(predictionTimerRef.current[field]);
    }
    predictionTimerRef.current[field] = setTimeout(() => {
      fetchPredictions(value, field);
    }, 180);
  }, [fetchPredictions]);

  const resolvePrediction = useCallback((prediction, onResolved) => {
    if (!prediction?.place_id || !geocoderRef.current) return;
    geocoderRef.current.geocode({ placeId: prediction.place_id }, (results = [], status) => {
      if (status !== 'OK' || !results[0]?.geometry?.location) return;
      const place = results[0];
      onResolved({
        address: place.formatted_address || prediction.description,
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      });
    });
  }, []);

  const resolveTypedAddress = useCallback((value) => {
    return new Promise((resolve) => {
      const query = String(value || '').trim();
      if (!query || !geocoderRef.current) {
        resolve(null);
        return;
      }
      geocoderRef.current.geocode({ address: query }, (results = [], status) => {
        if (status !== 'OK' || !results[0]?.geometry?.location) {
          resolve(null);
          return;
        }
        const place = results[0];
        resolve({
          address: place.formatted_address || query,
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        });
      });
    });
  }, []);

  const selectSourceSuggestion = (prediction) => {
    resolvePrediction(prediction, (resolved) => {
      setSource(resolved);
      setSourceInput(resolved.address);
      setSourceSuggestions([]);
      setActiveSuggestionField('');
    });
  };

  const selectDestinationSuggestion = (prediction) => {
    resolvePrediction(prediction, (resolved) => {
      setDestination(resolved);
      setDestinationInput(resolved.address);
      setDestinationSuggestions([]);
      setActiveSuggestionField('');
    });
  };

  const selectStopSuggestion = (prediction) => {
    resolvePrediction(prediction, (resolved) => {
      setCurrentStop(resolved.address);
      setStopSuggestions([]);
      setActiveSuggestionField('');
    });
  };

  // ✅ ADD STOP (Original Working Logic)
  const addStop = (e) => {
    e.preventDefault();
    if (!currentStop.trim()) return;

    setStops([...stops, { location: currentStop, stopover: true }]);
    setCurrentStop('');
    setStopSuggestions([]);
  };

  // ✅ REMOVE STOP
  const removeStop = (index) => {
    const updatedStops = stops.filter((_, i) => i !== index);
    setStops(updatedStops);
  };

  // ✅ DIRECTIONS CALLBACK
  const directionsCallback = useCallback((response) => {
    if (response?.status === 'OK') {
      setDirectionsResponse(response);
    }
  }, []);

  // ✅ SUBMIT HANDLER
  const handleSubmit = async () => {
    const resolvedSource = source || (await resolveTypedAddress(sourceInput));
    const resolvedDestination = destination || (await resolveTypedAddress(destinationInput));

    if (!routeName || !resolvedSource || !resolvedDestination) {
      alert("Route Name, Source and Destination required!");
      return;
    }

    const routeData = {
      routeName,
      source: resolvedSource,         
      destination: resolvedDestination,    
      stops: stops.map(s => ({ address: s.location })),
      routePath: extractSelectedRoutePath(directionsResponse?.routes?.[0]),
      selectedPathSummary: String(directionsResponse?.routes?.[0]?.summary || '').trim(),
      totalDistance: directionsResponse?.routes[0]?.legs.reduce((acc, leg) => acc + leg.distance.value, 0) || 0,
      totalDuration: directionsResponse?.routes[0]?.legs.reduce((acc, leg) => acc + leg.duration.value, 0) || 0
    };

    onSubmit(routeData);
  };

  if (!isLoaded)
    return <div className="ls-rt-overlay">Loading Map...</div>;

  return (
    <div className="ls-route-modal-root">
      <div className="ls-rt-overlay">
        <div className="ls-rt-container" style={{ maxWidth: '1300px' }}>

          {/* HEADER */}
          <div className="ls-rt-header">
            <h3>Add Route</h3>
            <button className="ls-rt-close" onClick={onClose}>&times;</button>
          </div>

          <div className="ls-rt-body">
            <div className="ls-rt-layout">

              {/* LEFT SIDE FORM */}
              <div className="ls-rt-form-section">
                
                <div className="ls-rt-group">
                  <label>Route Name *</label>
                  <input
                    type="text"
                    className="ls-rt-input"
                    value={routeName}
                    onChange={(e) => setRouteName(e.target.value)}
                  />
                </div>

                <div className="ls-rt-group">
                  <label>Source *</label>
                  <div className="ls-rt-autocomplete-wrap">
                    <input
                      className="ls-rt-input"
                      placeholder="Start Location"
                      value={sourceInput}
                      onFocus={() => setActiveSuggestionField('source')}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSourceInput(value);
                        setSource(null);
                        schedulePredictions(value, 'source');
                      }}
                    />
                    {activeSuggestionField === 'source' && sourceSuggestions.length > 0 && (
                      <div className="ls-rt-suggestions">
                        {sourceSuggestions.map((item) => (
                          <button
                            key={item.place_id}
                            type="button"
                            className="ls-rt-suggestion-item"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => selectSourceSuggestion(item)}
                          >
                            {item.description}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="ls-rt-group">
                  <label>Destination *</label>
                  <div className="ls-rt-autocomplete-wrap">
                    <input
                      className="ls-rt-input"
                      placeholder="End Location"
                      value={destinationInput}
                      onFocus={() => setActiveSuggestionField('destination')}
                      onChange={(e) => {
                        const value = e.target.value;
                        setDestinationInput(value);
                        setDestination(null);
                        schedulePredictions(value, 'destination');
                      }}
                    />
                    {activeSuggestionField === 'destination' && destinationSuggestions.length > 0 && (
                      <div className="ls-rt-suggestions">
                        {destinationSuggestions.map((item) => (
                          <button
                            key={item.place_id}
                            type="button"
                            className="ls-rt-suggestion-item"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => selectDestinationSuggestion(item)}
                          >
                            {item.description}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="ls-rt-group">
                  <label>Add Stops</label>
                  <div className="ls-rt-stop-row">
                    <div className="ls-rt-autocomplete-wrap">
                      <input
                        id="stop-input-unique"
                        className="ls-rt-input"
                        placeholder="Search stop..."
                        value={currentStop}
                        onFocus={() => setActiveSuggestionField('stop')}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCurrentStop(value);
                          schedulePredictions(value, 'stop');
                        }}
                      />
                      {activeSuggestionField === 'stop' && stopSuggestions.length > 0 && (
                        <div className="ls-rt-suggestions">
                          {stopSuggestions.map((item) => (
                            <button
                              key={item.place_id}
                              type="button"
                              className="ls-rt-suggestion-item"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => selectStopSuggestion(item)}
                            >
                              {item.description}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button className="ls-rt-add-btn" onClick={addStop}>
                      <Plus size={18} />
                    </button>
                  </div>

                  <div className="ls-rt-stops-list">
                    {stops.map((s, i) => (
                      <div key={i} className="ls-rt-stop-item">
                        <span className="ls-rt-stop-text"><MapPin size={14} /> {s.location}</span>
                        <button className="ls-rt-stop-del" onClick={() => removeStop(i)}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* STATS */}
                {directionsResponse && (
                  <div className="ls-rt-stats">
                    <div className="ls-rt-stat-box">
                      <span>Distance</span>
                      <strong>{(directionsResponse.routes[0].legs.reduce((acc, leg) => acc + leg.distance.value, 0) / 1000).toFixed(2)} km</strong>
                    </div>
                    <div className="ls-rt-stat-box">
                      <span>Duration</span>
                      <strong>{(directionsResponse.routes[0].legs.reduce((acc, leg) => acc + leg.duration.value, 0) / 60).toFixed(0)} mins</strong>
                    </div>
                  </div>
                )}
              </div>

              {/* RIGHT SIDE MAP */}
              <div className="ls-rt-map-section">
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={defaultCenter}
                  zoom={8}
                >
                  {source && destination && (
                    <DirectionsService
                      options={{
                        origin: source.address,
                        destination: destination.address,
                        waypoints: stops,
                        optimizeWaypoints: true,
                        travelMode: 'DRIVING'
                      }}
                      callback={directionsCallback}
                    />
                  )}
                  {directionsResponse && (
                    <DirectionsRenderer directions={directionsResponse} />
                  )}
                </GoogleMap>
              </div>

            </div>
          </div>

          <div className="ls-rt-footer">
            <button className="ls-rt-btn-main ls-rt-submit" onClick={handleSubmit}>SUBMIT</button>
            <button className="ls-rt-btn-main ls-rt-cancel" onClick={onClose}>CANCEL</button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AddRouteModal;
